/**
 * TradeInFlow — Carvana-style multi-step trade-in offer widget.
 *
 * Flow
 * ----
 *   Step 1 · Vehicle    — VIN decode OR license-plate-mock OR manual pick.
 *   Step 2 · Condition  — 4 large radio cards (Excellent/Good/Fair/Poor).
 *   Step 3 · Features   — optional check-boxes (leather / sunroof / nav /
 *                          premium wheels / heated seats) with $ adjustments.
 *   Step 4 · Offer      — reveal card: range + mid, CTAs to apply-to-inventory
 *                          or submit as a real cash-offer lead.
 *
 * Adjustments applied on top of the backend rule-based estimate:
 *   • Leather      +$300
 *   • Sunroof      +$400
 *   • Navigation   +$200
 *   • Prem. wheels +$150
 *   • Heated seats +$100
 *
 * `wants_cash_offer` toggle sends a `notes` marker + creates the trade-in
 * estimate lead with a "Cash offer requested" banner for BDC triage.
 *
 * Design pillars
 * --------------
 * • Left-aligned; step indicator across the top with a progress fill.
 * • Every button either a pill (small choices) or a big card (major forks).
 * • Gold-on-navy accent for the primary CTA, slate for secondary.
 * • Preserves the SessionStorage handoff to Inventory (belomotors_active_tradein_v1)
 *   so a customer who lands on `/sell-trade`, gets an offer, and clicks
 *   "Apply toward a vehicle" carries the credit to the finance calc.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Banknote, Car, Check, ChevronRight, Info, Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useTools } from "@/context/ToolsContext";

const CONDITIONS = [
  { value: "excellent", label: "Excellent", helper: "Like new, no visible issues, up-to-date service" },
  { value: "good",      label: "Good",      helper: "Normal wear, well-maintained, minor blemishes only" },
  { value: "fair",      label: "Fair",      helper: "Visible wear, or a mechanical issue or two" },
  { value: "poor",      label: "Poor",      helper: "Major cosmetic or mechanical concerns" },
];

const FEATURE_ADJUSTMENTS = [
  { key: "leather",   label: "Leather seats",  cents: 30000 },
  { key: "sunroof",   label: "Sunroof / moonroof", cents: 40000 },
  { key: "nav",       label: "Navigation",     cents: 20000 },
  { key: "wheels",    label: "Premium wheels", cents: 15000 },
  { key: "heated",    label: "Heated seats",   cents: 10000 },
  { key: "awd",       label: "AWD / 4WD",      cents: 25000 },
];

export const HOME_TRADEIN_SESSION_KEY = "belomotors_active_tradein_v1";

const fmtUSD = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format((cents || 0) / 100);

const StepDot = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2" data-testid={`tradein-step-dot-${n}`}>
    <span
      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
        done
          ? "bg-emerald-600 text-white"
          : active
            ? "bg-gold text-slate-950 shadow-md scale-110"
            : "bg-slate-200 text-slate-500"
      }`}
    >
      {done ? <Check size={13} /> : n}
    </span>
    <span className={`hidden sm:inline text-xs font-semibold ${active ? "text-slate-900" : done ? "text-slate-600" : "text-slate-400"}`}>
      {label}
    </span>
  </div>
);

const TradeInFlow = ({ linkedVehicleId = null, variant = "inline" }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setActiveTradeIn, activeTradeIn } = useTools() || {};

  const [step, setStep] = useState(1);

  // Step 1
  const [tab, setTab] = useState("vin");
  const [meta, setMeta] = useState(null);
  const [vin, setVin] = useState("");
  const [plate, setPlate] = useState("");
  const [plateState, setPlateState] = useState("MA");
  const [decoding, setDecoding] = useState(false);
  const [decoded, setDecoded] = useState(null);
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [trim, setTrim] = useState("");
  const [mileage, setMileage] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Step 2
  const [condition, setCondition] = useState("good");

  // Step 3
  const [features, setFeatures] = useState({});

  // Step 4
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [wantsCashOffer, setWantsCashOffer] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);

  useEffect(() => {
    api.get("/trade-in/metadata").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setContact((c) => ({
        name: c.name || user.full_name || "",
        email: c.email || user.email || "",
        phone: c.phone || user.phone || "",
        message: c.message,
      }));
    }
  }, [user]);

  const featureAdjCents = useMemo(
    () => FEATURE_ADJUSTMENTS.filter((f) => features[f.key]).reduce((s, f) => s + f.cents, 0),
    [features],
  );

  const decodeVin = async () => {
    const cleaned = vin.trim().toUpperCase();
    if (cleaned.length < 8) { toast.error("VIN must be at least 8 characters."); return; }
    setDecoding(true);
    try {
      const { data } = await api.get(`/trade-in/vin-decode/${cleaned}`);
      setDecoded(data);
      if (data.year) setYear(String(data.year));
      if (data.make) setMake(data.make);
      if (data.model) setModel(data.model);
      if (data.trim) setTrim(data.trim);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDecoding(false); }
  };

  const decodePlate = async () => {
    // Plate decoding is a paid service (LexisNexis / Carfax). We stub this with
    // a nice UX that instructs the user to fall back to VIN if the mock lookup
    // fails. In production this would call `/trade-in/plate-decode/{state}/{plate}`.
    if (!plate || plate.length < 3) { toast.error("Enter your license plate."); return; }
    toast.info("Plate lookup requires a paid data feed. Switching to manual entry — please fill in year/make/model.");
    setTab("manual");
  };

  const step1Complete = year && make && model && mileage;
  const step2Complete = !!condition;

  const submitEstimate = async () => {
    if (!step1Complete) return;
    setSubmitting(true);
    try {
      const body = {
        year: Number(year), make, model, trim: trim || null,
        mileage: Number(mileage),
        condition,
        zip_code: zipCode || null,
        vin: vin.trim().toUpperCase() || null,
        linked_vehicle_id: linkedVehicleId || null,
        notes: featureAdjCents > 0
          ? `Optional features (frontend-adjusted +${fmtUSD(featureAdjCents)}): ${FEATURE_ADJUSTMENTS.filter((f) => features[f.key]).map((f) => f.label).join(", ")}`
          : null,
      };
      const { data } = await api.post("/trade-in/estimate", body);
      // Frontend adjustments layered on top of backend estimate.
      const adjustedMid = data.estimate_mid_cents + featureAdjCents;
      const adjustedLow = data.estimate_low_cents + featureAdjCents;
      const adjustedHigh = data.estimate_high_cents + featureAdjCents;
      const enriched = {
        ...data,
        estimate_low_cents: adjustedLow,
        estimate_mid_cents: adjustedMid,
        estimate_high_cents: adjustedHigh,
        feature_adjustment_cents: featureAdjCents,
      };
      setResult(enriched);
      setStep(4);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const applyAndBrowse = () => {
    if (!result) return;
    const payload = {
      estimate_id: result.estimate_id,
      estimate_mid_cents: result.estimate_mid_cents,
      estimate_low_cents: result.estimate_low_cents,
      estimate_high_cents: result.estimate_high_cents,
      year: result.year, make: result.make, model: result.model,
      mileage: Number(mileage),
    };
    try { sessionStorage.setItem(HOME_TRADEIN_SESSION_KEY, JSON.stringify(payload)); } catch {}
    if (setActiveTradeIn) setActiveTradeIn(payload);
    toast.success(`${fmtUSD(result.estimate_mid_cents)} trade-in credit ready — apply it to any vehicle.`);
    navigate("/inventory");
  };

  const submitContact = async () => {
    if (!result?.estimate_id) return;
    if (!contact.name || !contact.email || !contact.phone) {
      toast.error("Please share your name, email, and phone.");
      return;
    }
    setContactSubmitting(true);
    try {
      const bodyMessage = [
        wantsCashOffer ? "[CASH OFFER REQUESTED]" : "",
        featureAdjCents > 0
          ? `Optional features (+${fmtUSD(featureAdjCents)}): ${FEATURE_ADJUSTMENTS.filter((f) => features[f.key]).map((f) => f.label).join(", ")}`
          : "",
        contact.message || "",
      ].filter(Boolean).join("\n");
      await api.post("/trade-in/estimate/submit", {
        estimate_id: result.estimate_id,
        name: contact.name, email: contact.email, phone: contact.phone,
        message: bodyMessage,
      });
      setContactSubmitted(true);
      toast.success(wantsCashOffer
        ? "Cash offer request sent — a BeloMotors specialist will call within 24 hours."
        : "Appraisal request sent — a BeloMotors specialist will reach out shortly.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setContactSubmitting(false); }
  };

  const years = useMemo(
    () => meta?.years || Array.from({ length: 22 }, (_, i) => 2026 - i),
    [meta],
  );

  const outerClass = variant === "embedded"
    ? "bg-white"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden";

  return (
    <section className={outerClass} data-testid="tradein-flow">
      <div className="px-5 sm:px-7 py-5 sm:py-6 bg-gold text-slate-950 rounded-t-2xl" data-testid="tradein-flow-header">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-slate-950 text-gold flex items-center justify-center">
              <Car size={17} />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-slate-950/60 font-bold">Trade-in</p>
              <p className="mt-0.5 font-display text-base sm:text-lg font-semibold">Get your instant offer</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <StepDot n={1} label="Vehicle"    active={step === 1} done={step > 1} />
            <ChevronRight size={12} className="text-slate-950/40" />
            <StepDot n={2} label="Condition" active={step === 2} done={step > 2} />
            <ChevronRight size={12} className="text-slate-950/40" />
            <StepDot n={3} label="Features"  active={step === 3} done={step > 3} />
            <ChevronRight size={12} className="text-slate-950/40" />
            <StepDot n={4} label="Offer"     active={step === 4} done={false} />
          </div>
        </div>
        {/* Mobile step progress */}
        <div className="sm:hidden mt-3 flex items-center gap-2 text-[11px] font-semibold text-slate-950/70">
          Step {step} of 4
          <div className="flex-1 h-1.5 bg-slate-950/15 rounded-full overflow-hidden">
            <div className="h-full bg-slate-950 transition-all" style={{ width: `${(step / 4) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-7">
        {step === 1 && (
          <div data-testid="tradein-step-1">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Step 1 · Your car</p>
            <h3 className="mt-1 font-display text-xl sm:text-2xl font-semibold text-slate-900">Tell us about your ride</h3>

            <div className="mt-4 grid grid-cols-3 gap-1.5" data-testid="tradein-input-tabs">
              {[
                { key: "vin",    label: "VIN" },
                { key: "plate",  label: "License plate" },
                { key: "manual", label: "Manual" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`py-2 rounded-lg text-xs font-semibold transition-colors ${
                    tab === t.key ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`tradein-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "vin" && (
              <div className="mt-4">
                <Label htmlFor="tradein-vin" className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">VIN</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    id="tradein-vin"
                    value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase())}
                    placeholder="1HGCM82633A004352"
                    maxLength={17}
                    className="font-mono text-sm"
                    data-testid="tradein-vin-input"
                  />
                  <Button onClick={decodeVin} disabled={decoding || vin.length < 8} data-testid="tradein-vin-decode-btn">
                    {decoding ? <Loader2 size={13} className="animate-spin" /> : "Decode"}
                  </Button>
                </div>
                {decoded?.year && (
                  <p className="mt-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 text-emerald-900">
                    <Sparkles size={11} className="text-emerald-500" />
                    <strong>{decoded.year} {decoded.make} {decoded.model}</strong>
                    {decoded.trim ? ` (${decoded.trim})` : ""}{decoded.cached ? " · cached" : ""}
                  </p>
                )}
              </div>
            )}

            {tab === "plate" && (
              <div className="mt-4 grid grid-cols-[1fr_100px_auto] gap-2 items-end">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">License plate</Label>
                  <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="ABC 1234" className="mt-1.5 font-mono text-sm" data-testid="tradein-plate-input" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">State</Label>
                  <Select value={plateState} onValueChange={setPlateState}>
                    <SelectTrigger className="mt-1.5" data-testid="tradein-plate-state"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="MA">MA</SelectItem><SelectItem value="NH">NH</SelectItem><SelectItem value="RI">RI</SelectItem><SelectItem value="CT">CT</SelectItem><SelectItem value="VT">VT</SelectItem><SelectItem value="ME">ME</SelectItem></SelectContent>
                  </Select>
                </div>
                <Button onClick={decodePlate} data-testid="tradein-plate-lookup">Lookup</Button>
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">Year</Label>
                <Select value={year} onValueChange={setYear}>
                  <SelectTrigger className="mt-1.5" data-testid="tradein-year-select"><SelectValue placeholder="Select year" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">Make</Label>
                {tab === "manual" ? (
                  <Select value={make} onValueChange={setMake}>
                    <SelectTrigger className="mt-1.5" data-testid="tradein-make-select"><SelectValue placeholder="Select make" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(meta?.makes || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={make} onChange={(e) => setMake(e.target.value)} placeholder="From VIN/plate" className="mt-1.5" data-testid="tradein-make-input" />
                )}
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">Model</Label>
                <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Accord" className="mt-1.5" data-testid="tradein-model-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">Trim <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input value={trim} onChange={(e) => setTrim(e.target.value)} placeholder="e.g. EX-L" className="mt-1.5" data-testid="tradein-trim-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">Mileage</Label>
                <Input type="number" min={0} max={500000} value={mileage} onChange={(e) => setMileage(e.target.value)} placeholder="90000" className="mt-1.5" data-testid="tradein-mileage-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">ZIP <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="02101" maxLength={10} className="mt-1.5" data-testid="tradein-zip-input" />
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <Button
                disabled={!step1Complete}
                onClick={() => setStep(2)}
                className="bg-slate-950 hover:bg-slate-800 text-white font-semibold"
                data-testid="tradein-next-1"
              >
                Continue <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="tradein-step-2">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Step 2 · Condition</p>
            <h3 className="mt-1 font-display text-xl sm:text-2xl font-semibold text-slate-900">
              How would you describe the car's condition?
            </h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="tradein-condition-cards">
              {CONDITIONS.map((c) => {
                const active = c.value === condition;
                return (
                  <button
                    key={c.value}
                    onClick={() => setCondition(c.value)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      active
                        ? "border-slate-950 bg-slate-950 text-white shadow-md"
                        : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
                    }`}
                    data-testid={`tradein-condition-${c.value}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? "border-gold bg-gold" : "border-slate-300"}`}>
                        {active && <span className="w-1.5 h-1.5 rounded-full bg-slate-950" />}
                      </span>
                      <span className="font-display font-semibold text-base">{c.label}</span>
                    </div>
                    <p className={`mt-1.5 text-xs ${active ? "text-white/70" : "text-slate-500"}`}>{c.helper}</p>
                  </button>
                );
              })}
            </div>

            <div className="mt-5 flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="tradein-back-2">
                <ArrowLeft size={14} /> Back
              </Button>
              <Button
                disabled={!step2Complete}
                onClick={() => setStep(3)}
                className="bg-slate-950 hover:bg-slate-800 text-white font-semibold"
                data-testid="tradein-next-2"
              >
                Continue <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="tradein-step-3">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Step 3 · Optional features</p>
            <h3 className="mt-1 font-display text-xl sm:text-2xl font-semibold text-slate-900">
              Add anything that makes your car stand out
            </h3>
            <p className="mt-1 text-sm text-slate-600">Extras that typically add to trade-in value.</p>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tradein-features-list">
              {FEATURE_ADJUSTMENTS.map((f) => {
                const on = !!features[f.key];
                return (
                  <button
                    key={f.key}
                    onClick={() => setFeatures((s) => ({ ...s, [f.key]: !s[f.key] }))}
                    className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                      on ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-400"
                    }`}
                    data-testid={`tradein-feature-${f.key}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`w-4 h-4 rounded border-2 flex items-center justify-center ${on ? "border-emerald-600 bg-emerald-600" : "border-slate-300"}`}>
                        {on && <Check size={11} className="text-white" strokeWidth={3} />}
                      </span>
                      <span className="text-sm font-medium text-slate-800">{f.label}</span>
                    </span>
                    <span className={`text-xs font-mono font-semibold ${on ? "text-emerald-700" : "text-slate-400"}`}>+{fmtUSD(f.cents)}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
              <p className="text-xs text-slate-600">Feature-based bump</p>
              <p className="font-display text-lg font-bold text-emerald-700" data-testid="tradein-feature-total">
                {featureAdjCents === 0 ? "$0" : `+${fmtUSD(featureAdjCents)}`}
              </p>
            </div>

            <div className="mt-5 flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="tradein-back-3">
                <ArrowLeft size={14} /> Back
              </Button>
              <Button
                onClick={submitEstimate}
                disabled={submitting}
                className="bg-gold hover:bg-gold-hover text-slate-950 font-semibold"
                data-testid="tradein-see-offer"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : (<>See my offer <ArrowRight size={14} /></>)}
              </Button>
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div data-testid="tradein-step-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Step 4 · Your offer</p>
            <h3 className="mt-1 font-display text-xl sm:text-2xl font-semibold text-slate-900">
              Estimated trade-in value
            </h3>

            <div className="mt-4 rounded-2xl bg-slate-950 text-white p-5 sm:p-6" data-testid="tradein-offer-card">
              <p className="text-[10px] uppercase tracking-[0.24em] text-white/60 font-bold">Mid-market value</p>
              <p className="mt-1.5 font-display text-4xl sm:text-5xl font-bold text-gold" data-testid="tradein-offer-mid">
                {fmtUSD(result.estimate_mid_cents)}
              </p>
              <p className="mt-2 text-sm text-white/80 font-mono">
                Range: <span className="text-gold">{fmtUSD(result.estimate_low_cents)}</span> — <span className="text-gold">{fmtUSD(result.estimate_high_cents)}</span>
              </p>
              <p className="mt-2 text-xs text-white/60">
                {result.year} {result.make} {result.model}{result.trim ? ` (${result.trim})` : ""} · {Number(mileage).toLocaleString()} mi · {condition}
              </p>
              {result.feature_adjustment_cents > 0 && (
                <p className="mt-1 text-xs text-white/60">Includes {fmtUSD(result.feature_adjustment_cents)} in feature bumps.</p>
              )}
              <p className="mt-3 text-[10px] text-white/50 flex items-start gap-1" data-testid="tradein-offer-disclaimer">
                <Info size={9} className="shrink-0 mt-0.5" />
                {result.disclaimer || "This is an estimate — final value confirmed at appraisal."}
              </p>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                onClick={applyAndBrowse}
                variant="outline"
                className="border-slate-950 text-slate-900 hover:bg-slate-950 hover:text-white"
                data-testid="tradein-apply-btn"
              >
                <Banknote size={14} /> Apply toward a car
              </Button>
              <Button
                onClick={() => setWantsCashOffer((v) => !v)}
                className={`${wantsCashOffer ? "bg-emerald-600 hover:bg-emerald-500" : "bg-gold hover:bg-gold-hover"} text-slate-950 font-semibold`}
                data-testid="tradein-cash-offer-toggle"
              >
                {wantsCashOffer ? "✓ Cash offer requested" : "Get a real cash offer"}
              </Button>
            </div>

            {!contactSubmitted ? (
              <div className="mt-5 border-t border-slate-200 pt-5" data-testid="tradein-contact-form">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
                  {wantsCashOffer ? "Cash offer — quick contact" : "Book your appraisal"}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  {wantsCashOffer
                    ? "A BeloMotors buyer will call within 24 hours with a firm cash offer. No obligation."
                    : "A BeloMotors specialist will confirm your trade-in value in person."}
                </p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Name</Label>
                    <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="tradein-contact-name" /></div>
                  <div><Label>Phone</Label>
                    <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="tradein-contact-phone" /></div>
                  <div className="sm:col-span-2"><Label>Email</Label>
                    <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="tradein-contact-email" /></div>
                  <div className="sm:col-span-2"><Label>Notes (optional)</Label>
                    <Textarea rows={2} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} placeholder="Best time to reach me…" data-testid="tradein-contact-message" /></div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={submitContact}
                    disabled={contactSubmitting}
                    className="bg-slate-950 hover:bg-slate-800 text-white font-semibold"
                    data-testid="tradein-contact-submit"
                  >
                    {contactSubmitting ? <Loader2 size={14} className="animate-spin" /> : (wantsCashOffer ? "Request cash offer" : "Book appraisal")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-4 text-sm text-emerald-900" data-testid="tradein-contact-success">
                <p className="font-semibold">✓ Request received.</p>
                <p className="mt-1">
                  {wantsCashOffer
                    ? "A BeloMotors buyer will call within 24 hours with a firm cash offer."
                    : "A BeloMotors specialist will reach out shortly to schedule your in-person appraisal."}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default TradeInFlow;
